const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const normalizeFive = (value) => (clamp(Number(value) || 3, 1, 5) - 1) * 25;

const effortFactor = (value) =>
  ({
    easy: 0.55,
    moderate: 0.85,
    challenging: 1.15,
    "very-challenging": 1.4,
    high: 1.35,
    variable: 0.95,
  })[value] || 0.85;

const parseEventDate = (event) => {
  if (!event?.date) return null;
  const [year, month, day] = String(event.date).split("-").map(Number);
  const [hour, minute] = String(event.time || "12:00").split(":").map(Number);
  const date = new Date(year, month - 1, day, hour, minute, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const RELEVANT_DEMAND_CATEGORIES = new Set([
  "workout", "sport", "competition", "recreation", "coaching",
  "group-class", "physiotherapy", "counselling", "work-demand",
  "caregiving", "travel",
]);

const eventContextFactor = (event = {}) => {
  const category = ({ workout: 1, sport: 1.05, competition: 1.25,
    recreation: 0.85, coaching: 0.9, "group-class": 1,
    physiotherapy: 0.65, counselling: 0.45, "work-demand": 0.8,
    caregiving: 0.7, travel: 0.65 })[event.category] || 1;
  const familiarity = ({ new: 1.2, some: 1.08, familiar: 1,
    "highly-familiar": 0.92 })[event.familiarity] || 1;
  const competition = ({ none: 1, practice: 1.05, competitive: 1.2,
    variable: 1.1 })[event.competitionContext] || 1;
  const recovery = ({ low: 0.85, moderate: 1, high: 1.25,
    variable: 1.12 })[event.recoveryCost] || 1;
  return category * familiarity * competition * recovery;
};

const isTrainingDemand = (event) =>
  RELEVANT_DEMAND_CATEGORIES.has(event?.category);

export function computeCalendarDemand(events = [], now = new Date()) {
  const nowMs = now.getTime();
  let recentLoad = 0;
  let directLoad = 0;
  let preparationLoad = 0;
  let provisionalLoad = 0;
  const recent = [];
  const direct = [];
  const preparation = [];
  const provisional = [];

  (events || []).forEach((event) => {
    if (!isTrainingDemand(event)) return;
    const date = parseEventDate(event);
    if (!date) return;
    const hours = (date.getTime() - nowMs) / 3600000;
    const durationHours = clamp(Number(event.durationMinutes) || 30, 5, 180) / 60;
    const demand = durationHours * effortFactor(event.usualEffort) * eventContextFactor(event);

    if (hours <= 1 && hours >= -36) {
      const recency = hours >= -12 ? 1 : hours >= -24 ? 0.75 : 0.5;
      recentLoad += demand * recency;
      recent.push(event);
    }
    if (hours > 1 && hours <= 6) {
      directLoad += demand;
      direct.push(event);
    } else if (hours > 6 && hours <= 24) {
      const proximity = hours <= 12 ? 0.8 : 0.55;
      preparationLoad += demand * proximity;
      preparation.push(event);
    } else if (hours > 24 && hours <= 48) {
      provisionalLoad += demand * 0.3;
      provisional.push(event);
    }
  });

  const recentScore = clamp(Math.round(recentLoad * 28), 0, 100);
  const directScore = clamp(Math.round(directLoad * 28), 0, 100);
  const preparationScore = clamp(Math.round(preparationLoad * 22), 0, 100);
  const provisionalScore = clamp(Math.round(provisionalLoad * 14), 0, 100);
  const upcomingScore = clamp(
    Math.round(directScore * 0.65 + preparationScore * 0.3 + provisionalScore * 0.05),
    0,
    100,
  );
  const combinedScore = clamp(
    Math.round(recentScore * 0.6 + directScore * 0.25 + preparationScore * 0.13 + provisionalScore * 0.02),
    0,
    100,
  );

  return {
    recentScore,
    directScore,
    preparationScore,
    provisionalScore,
    upcomingScore,
    combinedScore,
    recentCount: recent.length,
    upcomingCount: direct.length + preparation.length + provisional.length,
    recent,
    upcoming: [...direct, ...preparation, ...provisional],
    horizons: {
      direct0to6h: direct,
      preparation6to24h: preparation,
      provisional24to48h: provisional,
    },
  };
}

const energyBand = (value) => {
  const n = Number(value) || 3;
  if (n <= 2) return "low";
  if (n >= 4) return "high";
  return "mid";
};

const pressureBand = (value) => {
  const n = Number(value) || 3;
  if (n <= 2) return "low";
  if (n >= 4) return "high";
  return "mid";
};

const responseMagnitude = (entry) =>
  Number(entry?.responseMagnitude ?? entry?.changeMagnitude ?? entry?.improvementRating) || 0;

export function computePersonalResponseModifier(entries = [], checkIn = {}) {
  const primaryState = checkIn.primaryState || null;
  const energy = energyBand(checkIn.energy);
  const pressure = pressureBand(checkIn.actionPressure);
  const comparable = (entries || [])
    .filter((entry) => entry?.completed)
    .filter((entry) => !primaryState || entry.reportedStates?.[0] === primaryState)
    .filter((entry) => energyBand(entry.energyBefore) === energy)
    .filter((entry) => pressureBand(entry.actionPressureBefore) === pressure)
    .slice(0, 8);

  if (!comparable.length) {
    return { modifier: 0, confidence: "none", comparableCount: 0 };
  }

  let modifier = 0;
  comparable.forEach((entry, index) => {
    const recencyWeight = index < 2 ? 1 : index < 5 ? 0.7 : 0.45;
    const magnitude = responseMagnitude(entry);
    if (entry.feltWorse || entry.response === "worse") {
      modifier -= 10 * recencyWeight;
    } else if (magnitude >= 4 || entry.response === "better") {
      modifier += 3 * recencyWeight;
    } else if (magnitude === 1 || entry.response === "same") {
      modifier -= 2 * recencyWeight;
    }
  });

  return {
    modifier: clamp(Math.round(modifier), -20, 8),
    confidence: comparable.length >= 5 ? "moderate" : "early",
    comparableCount: comparable.length,
  };
}

const readinessCeiling = (value) =>
  ({
    pause: 0,
    modify: 45,
    "light-moderate": 65,
    unrestricted: 100,
  })[value] ?? 65;

const conditioningCeiling = (value) =>
  ({ beginner: 55, general: 72, advanced: 86, athlete: 100 })[value] ?? 72;

const validFive = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 1 && number <= 5 ? number : null;
};
const fivePointCeiling = (value) => {
  const number = validFive(value);
  return number == null ? 100 : Math.round(25 + (number - 1) * 18.75);
};
const meanFive = (values = []) => {
  const usable = values.map(validFive).filter(Number.isFinite);
  return usable.length ? usable.reduce((sum, number) => sum + number, 0) / usable.length : null;
};
const minimumFive = (values = []) => {
  const usable = values.map(validFive).filter(Number.isFinite);
  return usable.length ? Math.min(...usable) : null;
};

const recoveryResourceCeiling = ({ energy, hydration, nourishment }) => {
  let ceiling = 100;
  if (Number(energy) <= 1) ceiling = Math.min(ceiling, 45);
  else if (Number(energy) <= 2) ceiling = Math.min(ceiling, 62);

  if (["little", "unsure"].includes(hydration)) ceiling = Math.min(ceiling, 72);
  if (["not-recent", "unsure"].includes(nourishment)) ceiling = Math.min(ceiling, 72);
  if (
    ["little", "unsure"].includes(hydration) &&
    ["not-recent", "unsure"].includes(nourishment)
  ) {
    ceiling = Math.min(ceiling, 58);
  }
  return ceiling;
};

const bandFor = (value) => {
  if (value <= 20) return 1;
  if (value <= 40) return 2;
  if (value <= 60) return 3;
  if (value <= 80) return 4;
  return 5;
};

const bandLabel = (band) =>
  ({
    1: "Minimal relative demand",
    2: "Low relative demand",
    3: "Moderate relative demand",
    4: "Vigorous relative demand",
    5: "High/performance relative demand",
  })[band] || "Moderate relative demand";

export function fallbackForStateMatchBand(band) {
  if (band <= 1) return "somatic";
  if (band <= 3) return "moderate";
  return "primary";
}

export function computeFunctionalStateMatch({
  checkIn = {},
  calendarEvents = [],
  entries = [],
  nutritionContext = null,
  baselineVector = null,
  now = new Date(),
} = {}) {
  const mobilization = normalizeFive(checkIn.intensity);
  const actionPressure = normalizeFive(checkIn.actionPressure);
  const availableEnergy = normalizeFive(checkIn.energy);
  const somaticResponse = normalizeFive(checkIn.somaticResponse);
  const persistence = normalizeFive(checkIn.recoveryDifficulty);
  const cognitiveCapture = normalizeFive(checkIn.cognitiveCapture);

  const biologicalDemand = Math.round(
    0.3 * mobilization +
      0.2 * actionPressure +
      0.15 * availableEnergy +
      0.15 * somaticResponse +
      0.1 * persistence +
      0.1 * cognitiveCapture,
  );

  const behavioralChoice = clamp(Number(checkIn.behavioralChoice) || 3, 1, 5);
  const behavioralControlCeiling = Math.round(25 + (behavioralChoice - 1) * 18.75);

  const calendarDemand = computeCalendarDemand(calendarEvents, now);
  const loadCeiling = clamp(100 - Math.round(calendarDemand.combinedScore * 0.35), 55, 100);
  const resourceCeiling = recoveryResourceCeiling(checkIn);
  const nutritionPenalty = clamp(Number(nutritionContext?.nutritionLoadPenalty) || 0, 0, 30);
  const nutritionCeiling = 100 - nutritionPenalty;
  const baselineCapacityScore = minimumFive([
    baselineVector?.capacity?.movementFreedom,
    baselineVector?.capacity?.movementControl,
    baselineVector?.capacity?.trainingTolerance,
    baselineVector?.capacity?.activityFamiliarity,
    baselineVector?.capacity?.recentLoadManageability,
  ]);
  const baselineResourceScore = meanFive([
    baselineVector?.resources?.sleepRestoration,
    baselineVector?.resources?.sleepConsistency,
    baselineVector?.resources?.nourishment,
    baselineVector?.resources?.hydration,
    baselineVector?.resources?.recoveryOpportunity,
    baselineVector?.resources?.routineFit,
  ]);
  const baselineCapacityCeiling = fivePointCeiling(baselineCapacityScore);
  const baselineResourceCeiling = fivePointCeiling(baselineResourceScore);
  const capacityCeiling = Math.min(
    readinessCeiling(checkIn.physicalReadiness),
    conditioningCeiling(checkIn.conditioningLevel),
    resourceCeiling,
    loadCeiling,
    nutritionCeiling,
    baselineCapacityCeiling,
    baselineResourceCeiling,
  );

  const personal = computePersonalResponseModifier(entries, checkIn);
  const requestedOutput = clamp(biologicalDemand + personal.modifier, 0, 100);
  const finalRelativeOutput = Math.round(
    Math.min(requestedOutput, behavioralControlCeiling, capacityCeiling),
  );
  const finalBand = bandFor(finalRelativeOutput);

  const ceilingReasons = [];
  if (behavioralControlCeiling < requestedOutput) ceilingReasons.push("behavioral choice");
  if (capacityCeiling < requestedOutput) ceilingReasons.push("current physical/recovery capacity");
  if (nutritionPenalty > 0) ceilingReasons.push("reported nutrition/recovery response");
  if (baselineCapacityCeiling < requestedOutput) ceilingReasons.push("baseline physical capacity");
  if (baselineResourceCeiling < requestedOutput) ceilingReasons.push("baseline recovery resources");
  if (calendarDemand.combinedScore >= 30) ceilingReasons.push("recent or upcoming scheduled demand");

  return {
    modelVersion: "functional-state-match-v3",
    principle: "Nervous-system state matching + nervous-system training = regulation",
    biologicalDemand,
    behavioralControlCeiling,
    capacityCeiling,
    requestedOutput,
    finalRelativeOutput,
    finalBand,
    finalBandLabel: bandLabel(finalBand),
    recommendedFallbackLevel: fallbackForStateMatchBand(finalBand),
    calendarDemand,
    baselineContext: baselineVector
      ? {
capacityScore: baselineCapacityScore,
capacityCeiling: baselineCapacityCeiling,
recoveryResourceScore: baselineResourceScore,
recoveryResourceCeiling: baselineResourceCeiling,
environmentalSensitivity: validFive(baselineVector?.environment?.sensitivity),
coachingResponse: validFive(baselineVector?.connection?.coachingResponse),
        }
      : null,
    nutritionContext: nutritionContext
      ? {
          direction: nutritionContext.direction,
          energyGapPercent: nutritionContext.energyGapPercent,
          nutritionLoadPenalty,
          nervousSystemNotes: nutritionContext.nervousSystemNotes || [],
        }
      : null,
    personalResponse: personal,
    components: {
      mobilization,
      actionPressure,
      availableEnergy,
      somaticResponse,
      persistence,
      cognitiveCapture,
      behavioralChoice: behavioralControlCeiling,
    },
    explanation:
      finalRelativeOutput < requestedOutput
        ? `The present nervous-system response supports output, but ${ceilingReasons.join(", ") || "current capacity"} lowers the appropriate relative dose.`
        : "The present nervous-system response and available capacity support the calculated relative dose without an additional ceiling being reached.",
  };
}
